import { useEffect } from 'react'
import { useRouter } from 'next/router'

import ROUTES from '~/shared/constants/routes.const'
import useGetGradePageRoute from '~/dashboard/hooks/useGetGradePageRoute'
import { selectFields } from '~/settings-scenario/redux/settings.slice'
import { setGradeListByCountryActionCreator } from '~/curriculum/slice/curriculum.slice'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { useGetCurriculumCountryCodeFromCurriculumName } from '~/curriculum/hooks/useGetCurriculumCountryCode'
import { isAuthenticated } from '~/auth/slices/auth.slice'
import { setCookie } from '~/utils/cookie.utils'

import { setLockRedirectionActionCreator } from '../slice/redirection.slice'

import getLanguageFromCurriculumCode from '../utils/getLanguageFromCurriculumCode'

export default function useHandleRedirectionOnChange(isOnboarding?: boolean): () => Promise<void> {
  const router = useRouter()
  const dispatch = useAppDispatch()

  const authenticated = useAppSelector(isAuthenticated)
  const fields = useAppSelector(selectFields)
  const curriculumCountryCode = useGetCurriculumCountryCodeFromCurriculumName(fields.curriculum)
  const gradeUrl = useGetGradePageRoute({
    isOnboarding,
    specificCountryCode: curriculumCountryCode,
    isCurriculum: true,
  })

  useEffect(() => {
    router.prefetch(gradeUrl)
  }, [gradeUrl, router])

  const handleRedirection = async (): Promise<void> => {
    const isSettingsPage = router.pathname.includes(ROUTES.SETTINGS)
    const isCampaignPage = router.pathname.includes(ROUTES.CAMPAIGNS)
    const isPartnerPage = router.pathname.includes(ROUTES.PARTNER)
    const redirectToCurriculum =
      router.pathname.includes(ROUTES.ONBOARDING) || router.pathname.includes(ROUTES.BOOTCAMP_SELECT_GRADE)
    const languageKey = getLanguageFromCurriculumCode(curriculumCountryCode)

    dispatch(setGradeListByCountryActionCreator([]))

    // Released in `finally` because a latch outlives the tab session, keeping _app's language
    // redirect off for the next login or profile switch.
    try {
      if (!authenticated) {
        setCookie({ name: 'curriculumCountry', value: curriculumCountryCode, expireInDays: 365 })

        if (router.pathname === ROUTES.HOME) {
          await router.push(ROUTES.HOME, ROUTES.HOME, { locale: languageKey })
          return
        }
      }

      if (redirectToCurriculum) {
        await router.push(gradeUrl, gradeUrl, { locale: languageKey })
      } else if (isSettingsPage || isCampaignPage || isPartnerPage) {
        await router.push(router.asPath, router.asPath, { locale: languageKey })
      } else {
        await router.push(ROUTES.DASHBOARD, ROUTES.DASHBOARD, { locale: languageKey })
      }
    } finally {
      dispatch(setLockRedirectionActionCreator(false))
    }
  }

  return handleRedirection
}
